/**
 * Crash reports, the pure part: redaction, the report text the crash screen shows, and the request "Send to Lab43"
 * posts to Lab43's feedback system (lab43-auth docs/CRASH-REPORTS.md). Nothing is sent without that click.
 *
 * No imports, so it runs in Node for the tests (apps/web/scripts/crash-report.test.ts). Collecting the facts, the
 * console ring buffer and the crash screen are lib/crash.ts and ui/CrashScreen.tsx.
 *
 * Never in a report: plan, world, schematic or file names, plan contents, invite links, the account's email or ids,
 * tokens, anything from Mojang's assets, file paths with a user name. The facts are counts and versions; free text
 * (error messages, stacks, console lines) goes through `redact()` with the names the app knows (`secrets`).
 */

export const CRASH_REPORTS_URL = "https://auth.lab43.dev/api/v1/crash-reports";

/** limits of the request (CRASH-REPORTS.md) */
const MAX_TITLE = 300;
const MAX_FINGERPRINT = 500;
const MAX_APP_VERSION = 60;
const MAX_PLATFORM = 200;
const MAX_REPORT = 200_000;
export const MAX_NOTE = 2000;
/** ours: stacks and console lines are cut to these */
const MAX_STACK = 16_000;
/** the account site takes `details` up to 100 KB (as stored); ours leaves room */
const MAX_DETAILS_BYTES = 95_000;
const MAX_COMPONENT_STACK = 8_000;
export const MAX_LOG_LINES = 30;
export const MAX_LOG_LINE = 400;

export interface UaHints {
  platform?: string;
  platformVersion?: string;
  architecture?: string;
  bitness?: string;
}

export interface WebGlFacts {
  version?: string;
  renderer?: string;
  vendor?: string;
  maxTextureSize?: number;
  maxArrayLayers?: number;
  max3dTextureSize?: number;
  maxTextureUnits?: number;
  maxRenderbufferSize?: number;
  contextLost?: boolean;
}

export interface CrashFacts {
  kind: "crash" | "error";
  /** react: the app stopped drawing (error boundary) · repeated: the same uncaught error kept coming (every frame) */
  where: "react" | "repeated";
  error: { name: string; message: string; stack?: string };
  componentStack?: string;
  /** how often the uncaught error came (where = repeated) */
  repeats?: number;
  /** version of the build; build = "<date> · <commit>"; desktop = the desktop app's version (null in a browser) */
  app: { version: string; build?: string; desktop: string | null };
  userAgent: string;
  uaHints?: UaHints;
  screen?: { w: number; h: number; dpr: number };
  uptimeS: number;
  memory?: { usedMB?: number; totalMB?: number; limitMB?: number; deviceGB?: number; cpus?: number };
  /** null: no WebGL 2 at all */
  webgl?: WebGlFacts | null;
  world?: {
    version?: string;
    dataVersion?: number;
    /** dimensions = Minecraft 26.x folders (dimensions/<ns>/<dim>/), legacy = 1.18–1.21 (DIM-1, DIM1) */
    layout?: "dimensions" | "legacy";
    modded?: boolean;
    dimensions?: number;
    regionsHere?: number;
    /** streamed from a shared world (guest) */
    streamed?: boolean;
    mapColoursOnly?: boolean;
    demo?: boolean;
  } | null;
  view?: { chunks: number; regionsLoaded: number; regionsTotal: number; pending: number; triangles: number; fps: number; renderDistance?: number } | null;
  plan?: {
    objects: number;
    /** per kind: path, area, building, marker */
    byKind: Record<string, number>;
    groups: number;
    schematicBuildings: number;
    here: number;
    readOnly?: boolean;
    role?: string;
    live?: string;
    peers?: number;
  } | null;
  ui?: { phase: string; tool: string; panel?: string | null; board?: boolean; slice?: boolean; topDown?: boolean; lighting?: boolean; help?: boolean };
  /** recent console errors and warnings, oldest first */
  log: string[];
  /** names and ids the app knows that must never leave (world, plan, file and schematic names, account, invite) */
  secrets: string[];
}

export interface CrashReport {
  kind: "crash" | "error";
  component: "web" | "desktop";
  title: string;
  fingerprint?: string;
  appVersion: string;
  platform: string;
  /** the whole text the person sees, and what is sent as `report` */
  report: string;
  details: Record<string, unknown>;
}

// ---------------------------------------------------------------- redaction

/**
 * default object names, everyday words and the labels redaction puts in: never treated as a private name (it would
 * blank out normal text, or turn "<path>" into "<<name>>")
 */
const SAFE_WORDS = new Set([
  "path", "area", "building", "marker", "paths", "areas", "buildings", "markers", "home", "group", "layout", "template",
  "schematic", "world", "new world", "plan", "untitled", "minecraft", "overworld", "nether", "the end", "error", "null",
  "undefined", "true", "false", "mc planner", "lab43", "demo island",
  "name", "local", "file", "local file", "token", "redacted", "email", "hex",
  // the folders the path rules know (a plan called "Users" mustn't keep "/Users/robin/" from going)
  "users", "media", "mnt", "volumes", "var", "tmp", "private", "run", "opt", "srv", "root", "saves",
]);

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** a path's last part: a file with a known extension may hold spaces ("Robin's Base.zip"), anything else ends at one */
const LAST_SEGMENT = String.raw`(?:[^\\/\r\n"<>|*?:]*?\.(?:zip|mcplan|json|litematic|schem|schematic|nbt|jar|mca|mcc|dat|png|jpe?g|webp|txt|log)(?![\w.])|[^\\/\s"<>|*?:]*)`;

/** a region / level file at the end of a path is a game file, not a name: kept so the report says what was read */
const GAME_FILE = /^(?:r\.-?\d+\.-?\d+\.mc[ac]|c\.-?\d+\.-?\d+\.mcc|level\.dat(?:_old)?|session\.lock)$/;

function pathOut(path: string): string {
  const tail = path.split(/[\\/]/).pop() ?? "";
  return GAME_FILE.test(tail) ? `<path>/${tail}` : "<path>";
}

/** not an IP address: a version number ("Chrome 141.0.7390.55", "Java 21.0.2.1") */
function versionBefore(text: string, at: number): boolean {
  return /(?:version|chrome|edg|firefox|safari|webkit|java|jdk|build|driver|\bv|[a-z]\/)\s*[:=]?\s*$/i.test(text.slice(Math.max(0, at - 16), at));
}

/** what follows a world's own folder in a path: its level file, or one of its folders */
const WORLD_INSIDE = String.raw`(?:level\.dat(?:_old)?\b|(?:region|dimensions|DIM-?1|DIM1|entities|poi|playerdata)[\\/]|data[\\/][\w.-]+\.dat\b)`;
/** cheap test before the real rule: does the line hold a part of a world at all? */
const HAS_WORLD_INSIDE = new RegExp(String.raw`(?:^|[\\/\s("'])${WORLD_INSIDE}`);
/**
 * the folders before the first of them ("My World/region/r.0.0.mca", "saves/Robin's Base/level.dat", Minecraft's own
 * "New World (1)", "[SMP] Base", "zip:My World/…"): at most 8 of up to 120 characters each, so a long line can't make
 * the search slow. A folder name may hold spaces, so the words before it on the line can go too: better than a name
 * getting out. A "(" or "[" that doesn't close inside the folder name ("method 9 (My World/…") isn't part of it.
 */
const WORLD_FOLDERS = new RegExp(
  String.raw`(?<![\w.<>/\\-])(?:(?:[^\\/\r\n"<>|*?:()\[\]]|\(\d{1,4}\)|\[[^\]\\/\r\n"<>|*?:]{1,40}\]){1,120}[\\/]){1,8}?(?=${WORLD_INSIDE})`,
  "g",
);

function worldFolders(s: string): string {
  if (!HAS_WORLD_INSIDE.test(s)) return s;
  return s.split("\n").map((line) => (HAS_WORLD_INSIDE.test(line) ? line.replace(WORLD_FOLDERS, "<world>/") : line)).join("\n");
}

/**
 * Removes what must never leave this computer from free text. `secrets` are names the app knows (world, plans,
 * files, the account); everything else is found by its shape: tokens, keys, emails, local paths and file URLs,
 * world folders, ids, IP addresses. A name is marked first and labelled last, so a path holding it goes whole
 * ("<path>", never "<path><name> 2" or "C:\\x\\<name> Village").
 */
export function redact(input: string, secrets: readonly (string | null | undefined)[] = []): string {
  if (!input) return "";
  return redactShapes(markNames(redactSecrets(clean(input)), secrets)).replaceAll(NAME_MARK, "<name>");
}

/**
 * A stack: the shapes everywhere, the names only in lines that aren't frames. A frame ("at render (index.js:1:2)",
 * "render@tauri://localhost/assets/index.js:1:2") is the app's own code: a plan called "render" mustn't blank it out.
 */
export function redactStack(input: string, secrets: readonly (string | null | undefined)[] = []): string {
  if (!input) return "";
  return clean(input)
    .split("\n")
    .map((line) => (isFrame(line) ? redactShapes(redactSecrets(line)) : redactShapes(markNames(redactSecrets(line), secrets)).replaceAll(NAME_MARK, "<name>")))
    .join("\n");
}

/** a stack frame with its location: V8 ("at fn (url:1:2)", "at url:1:2", "at Array.map (<anonymous>)") or WebKit and
 * Gecko ("fn@url:1:2", "fn@[native code]") */
function isFrame(line: string): boolean {
  const t = line.trim();
  return (
    /^at\s.*\((?:\S+:\d+(?::\d+)?|<anonymous>|native|index \d+)\)$/.test(t) ||
    /^at\s+(?:async\s+)?[a-z][\w+.-]*:\S+:\d+(?::\d+)?$/i.test(t) ||
    /^[^\s@]*@(?:[a-z][\w+.-]*:\S+:\d+(?::\d+)?|\[native code\])$/i.test(t)
  );
}

/** where a name was, until the shapes are done: one character the path rules take as part of a folder or file name */
const NAME_MARK = "\uE000";

/** control characters (a log's colours) can't be stored, the account site drops them too; and no stray name mark */
function clean(input: string): string {
  return String(input).replace(/\x1b\[[0-9;]*m|[\x00-\x08\x0b\x0c\x0e-\x1f\x7f\uE000]/g, "");
}

/** the names the app knows: longest first, case-insensitive, never inside another word; also as they look in a URL */
function markNames(input: string, secrets: readonly (string | null | undefined)[]): string {
  let s = input;
  const names = [...new Set(secrets.map((x) => (typeof x === "string" ? x.trim() : "")).filter((x) => x.length >= 3 && !SAFE_WORDS.has(x.toLowerCase())))];
  const forms = new Set<string>();
  for (const n of names) {
    forms.add(n);
    try {
      const enc = encodeURIComponent(n);
      if (enc !== n) forms.add(enc);
    } catch { /* lone surrogate */ }
  }
  let lower = s.toLowerCase();
  for (const n of [...forms].sort((a, b) => b.length - a.length)) {
    if (!lower.includes(n.toLowerCase())) continue; // most names aren't there: skip the regex
    s = s.replace(new RegExp(`(?<![\\p{L}\\p{N}_])${escapeRe(n)}(?![\\p{L}\\p{N}_])`, "giu"), NAME_MARK);
    lower = s.toLowerCase();
  }
  return s;
}

/**
 * Secrets found by their shape, before the names are marked (a plan called "Server" or "Code" mustn't break the rule
 * that takes "&server=…" or "?code=…" out): tokens, keys, "password: …", emails.
 */
function redactSecrets(input: string): string {
  return input
    // secrets in a link's query or fragment (invite links, OAuth codes, signed URLs)
    .replace(/([?&#](?:access_token|refresh_token|id_token|token|code|key|apikey|api_key|invite|join|server|state|sig|signature|secret)=)[^&#\s"'<>)]+/gi, "$1<redacted>")
    // JSON web tokens (Supabase access tokens, Minecraft sessions)
    .replace(/\beyJ[\w-]{5,}\.[\w-]{5,}\.[\w-]*/g, "<token>")
    .replace(/\bBearer\s+[\w.~+/=-]+/gi, "Bearer <token>")
    // keys with a known prefix (Supabase, Stripe, Lab43 device tokens and sign-in keys)
    .replace(/\b(?:sb_(?:secret|publishable)_|sk_(?:live|test)_|rk_(?:live|test)_|pk_(?:live|test)_|whsec_|l43d_|l43k_|hqs_)[\w-]+/g, "<token>")
    // "token=…", "password: …", "apiKey": "…"
    .replace(/\b(access[_-]?token|refresh[_-]?token|id[_-]?token|token|password|passwd|secret|api[_-]?key|session[_-]?id|cookie|authorization)(["']?\s*[:=]\s*["']?)(?!<)[^\s"',;&}]+/gi, "$1$2<redacted>")
    .replace(/(?<![\w.+-])[\w.+-]{1,64}@[\w-]{1,63}(?:\.[\w-]{1,63}){1,8}/g, "<email>");
}

/** everything else found by its shape (see redact) */
function redactShapes(input: string): string {
  let s = input;
  s = s
    // an email whose local part was a known name ("<mark>@example.com")
    .replace(/(?<![\w.+\uE000-])[\w.+\uE000-]{1,64}@[\w-]{1,63}(?:\.[\w-]{1,63}){1,8}/g, "<email>")
    // files on this computer: the desktop app's asset protocol, file URLs, absolute paths (may hold a user name). The
    // desktop app's own code is tauri://localhost/… (macOS, Linux) or http(s)://tauri.localhost/… (Windows): it stays,
    // so its stack frames and the fingerprint do too.
    .replace(/\b(?:asset|file):\/\/[^\s"'<>)]+/gi, "<local file>")
    .replace(/\btauri:\/\/(?!localhost\/)[^\s"'<>)]+/gi, "<local file>")
    .replace(/\bhttps?:\/\/asset\.localhost\/[^\s"'<>)]+/gi, "<local file>")
    // (an apostrophe can be part of a folder name: C:\Users\O'Brien)
    .replace(/\\\\[^\\\s"<>|]+\\[^\s"<>|]*/g, "<path>")
    .replace(new RegExp(`\\b[A-Za-z]:[\\\\/](?:[^\\\\/\\r\\n"<>|*?:]+[\\\\/])*${LAST_SEGMENT}`, "g"), pathOut)
    .replace(new RegExp(`(?<![\\w.:/])/(?:home|Users|mnt|media|Volumes|var|tmp|private|run|opt|srv|root)/(?:[^/\\r\\n"<>|]+/)*${LAST_SEGMENT}`, "g"), pathOut)
    .replace(new RegExp(`(?<![\\w/])~[\\\\/](?:[^\\\\/\\r\\n"<>|]+[\\\\/])*${LAST_SEGMENT}`, "g"), pathOut)
    // ids (account ids, room ids)
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "<id>")
    // IP addresses (loopback stays: it says "this computer")
    .replace(/(?<![\w:])(?:[0-9a-f]{1,4}:){7}[0-9a-f]{1,4}(?![\w:])/gi, "<ip>")
    .replace(/(?<![\w:])(?:[0-9a-f]{1,4}:){1,6}:(?:[0-9a-f]{1,4}(?::[0-9a-f]{1,4}){0,5})?(?![\w:])/gi, (m) => (m === "::1" ? m : "<ip>"))
    .replace(/(?<![\w.])(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}(?!\w|\.\d)/g, (m, at: number, all: string) =>
      m === "127.0.0.1" || m === "0.0.0.0" || versionBefore(all, at) ? m : "<ip>")
    // long hex strings and token-like runs (hashes of files, session keys); every pattern here starts only where a run
    // starts (look-behind), so a long input can't make it backtrack
    .replace(/(?<![0-9a-z_])[0-9a-f]{32,}(?![0-9a-z_])/gi, (m) => (/[a-f]/i.test(m) && /\d/.test(m) ? "<hex>" : m))
    .replace(/(?<![\w-])[\w-]{40,}(?![\w-])/g, (m) => ((m.match(/\d/g)?.length ?? 0) >= 3 && /[a-z]/.test(m) && /[A-Z]/.test(m) ? "<token>" : m));
  return worldFolders(s);
}

// ---------------------------------------------------------------- stacks

/** "index-B2x9kLmQ.js" → "index.js", "/src/lib/plan.ts?t=1" → "plan.ts", "blob:https://…/<uuid>" → "blob" */
function sourceFile(url: string): string {
  if (/^blob:/i.test(url)) return "blob";
  const file = url.split(/[?#]/)[0].split(/[\\/]/).pop() || url;
  return file.replace(/-[A-Za-z0-9_-]{8}(?=\.m?js$)/, "");
}

/**
 * The top stack frame that has a source file, without line and column: "at planSummary (plan.ts)". Groups the same
 * crash in Lab43 HQ. Chrome ("at fn (url:1:2)"), Firefox and Safari ("fn@url:1:2") stacks.
 */
export function fingerprint(stack: string | undefined): string | undefined {
  for (const raw of (stack ?? "").split("\n")) {
    const line = raw.trim();
    const v8 = line.match(/^at\s+(?:async\s+)?(?:(.*?)\s+\()?((?:[a-z][\w+.-]*:)?[^\s()]+?):\d+(?::\d+)?\)?$/i);
    const gecko = !v8 && line.match(/^(.*?)@((?:[a-z][\w+.-]*:)?\S+?):\d+(?::\d+)?$/i);
    const m = v8 || gecko;
    if (!m) continue;
    const fn = (m[1] || "").replace(/^async\*/, "").trim() || "<anonymous>";
    return `at ${fn} (${sourceFile(m[2])})`.slice(0, MAX_FINGERPRINT);
  }
  return undefined;
}

// ---------------------------------------------------------------- the browser and the system

/** "Chrome 141" · "Windows 10/11 x64" · webview: what draws the desktop app ("WebView2 141"), null in a browser */
export function describeUserAgent(ua: string, hints: UaHints = {}, desktop = false): { browser: string; os: string; webview: string | null } {
  const v = (re: RegExp) => ua.match(re)?.[1];
  let os = "Unknown OS";
  const winHint = hints.platform === "Windows" ? Number.parseInt(hints.platformVersion ?? "", 10) : NaN;
  if (/Windows NT/.test(ua)) {
    const nt = v(/Windows NT ([\d.]+)/);
    os = Number.isFinite(winHint) && winHint > 0 ? (winHint >= 13 ? "Windows 11" : "Windows 10")
      : nt === "10.0" ? "Windows 10/11" : nt === "6.3" ? "Windows 8.1" : nt === "6.1" ? "Windows 7" : `Windows NT ${nt ?? ""}`.trim();
  } else if (/iPhone|iPad|iPod/.test(ua)) os = `iOS ${(v(/OS (\d+(?:_\d+)?)/) ?? "").replace("_", ".")}`.trim();
  else if (/Android/.test(ua)) os = `Android ${v(/Android ([\d.]+)/) ?? ""}`.trim();
  else if (/Mac OS X|Macintosh/.test(ua)) os = hints.platform === "macOS" && hints.platformVersion ? `macOS ${hints.platformVersion}` : "macOS";
  else if (/CrOS/.test(ua)) os = "ChromeOS";
  else if (/Linux/.test(ua)) os = "Linux";
  const arch = hints.architecture
    ? hints.architecture === "x86" ? (hints.bitness === "64" ? "x64" : "x86") : hints.architecture === "arm" ? (hints.bitness === "64" ? "arm64" : "arm") : hints.architecture
    : /Win64|x64|x86_64|amd64/i.test(ua) ? "x64" : /arm64|aarch64/i.test(ua) ? "arm64" : "";
  if (arch && !/iOS|Android/.test(os)) os += ` ${arch}`;

  let browser = "Unknown browser";
  const edge = v(/Edg(?:e|A|iOS)?\/(\d+)/), opera = v(/OPR\/(\d+)/), firefox = v(/(?:Firefox|FxiOS)\/(\d+)/);
  const chrome = v(/(?:Chrome|CriOS)\/(\d+)/), safari = /Safari\//.test(ua) ? v(/Version\/(\d+(?:\.\d+)?)/) : undefined;
  if (edge) browser = `Edge ${edge}`;
  else if (opera) browser = `Opera ${opera}`;
  else if (firefox) browser = `Firefox ${firefox}`;
  else if (chrome) browser = `Chrome ${chrome}`;
  else if (safari) browser = `Safari ${safari}`;
  else if (/AppleWebKit/.test(ua)) browser = `WebKit ${v(/AppleWebKit\/([\d.]+)/) ?? ""}`.trim();

  let webview: string | null = null;
  if (desktop) {
    if (os.startsWith("Windows")) webview = edge ? `WebView2 ${edge}` : "WebView2";
    else if (os.startsWith("macOS")) webview = "WKWebView";
    else if (os.startsWith("Linux")) webview = `WebKitGTK${/AppleWebKit\/([\d.]+)/.test(ua) ? ` (WebKit ${v(/AppleWebKit\/([\d.]+)/)})` : ""}`;
    else webview = browser;
  }
  return { browser, os, webview };
}

// ---------------------------------------------------------------- the report

const cut = (s: string, n: number) => (s.length <= n ? s : `${s.slice(0, Math.max(0, n - 1))}…`);
const oneLine = (s: string) => s.replace(/\s+/g, " ").trim();
const yesNo = (b: boolean | undefined) => (b ? "yes" : "no");
const num = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} M` : n >= 10_000 ? `${Math.round(n / 1000)} k` : String(Math.round(n)));

export function duration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min ${s % 60} s`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Builds the report from the collected facts: everything in it is redacted. */
export function buildCrashReport(f: CrashFacts): CrashReport {
  const secrets = f.secrets ?? [];
  // cut before redacting (bounded work), then cut again after: what a pre-cut could split never reaches the report
  const r = (s: string | undefined, pre = 2000) => redact(cut(s ?? "", pre), secrets);
  const desktop = f.app.desktop !== null;
  const ua = describeUserAgent(f.userAgent, f.uaHints, desktop);
  const errName = cut(oneLine(r(f.error.name, 400)), 100) || "Error";
  const errMessage = oneLine(r(f.error.message, 4 * MAX_TITLE));
  const title = cut(errMessage ? `${errName}: ${errMessage}` : errName, MAX_TITLE);
  // stacks: names only outside the frames (redactStack), so the app's own function and file names stay, and so does
  // the fingerprint
  let stack = cut(redactStack(cut(f.error.stack ?? "", 4 * MAX_STACK), secrets).trim(), MAX_STACK);
  let componentStack = cut(redactStack(cut(f.componentStack ?? "", 4 * MAX_COMPONENT_STACK), secrets).replace(/^\s*\n/, "").trimEnd(), MAX_COMPONENT_STACK);
  const fp = fingerprint(stack);
  let log = f.log.slice(-MAX_LOG_LINES).map((l) => cut(oneLine(r(l, 4 * MAX_LOG_LINE)), MAX_LOG_LINE));
  const version = desktop && f.app.desktop ? f.app.desktop : f.app.version;
  const build = f.app.build ? cut(r(f.app.build, 200), 40) : "";
  const appVersion = cut(build ? `${version} (${build})` : version, MAX_APP_VERSION);
  const platform = cut(desktop ? `${ua.os} · desktop app ${f.app.desktop || "?"} · ${ua.webview}` : `${ua.os} · ${ua.browser}`, MAX_PLATFORM);

  const lines: string[] = [];
  const add = (label: string, value: string | undefined | null) => { if (value) lines.push(`${label}: ${value}`); };
  lines.push("MC Planner crash report");
  add("Error", title);
  add("Where", f.where === "react" ? "the app stopped drawing (React)" : `an uncaught error that kept coming back (${plural(f.repeats ?? 1, "time")})`);
  add("Fingerprint", fp);
  lines.push("");
  add("App", `MC Planner ${f.app.version}${build ? ` · build ${build}` : ""}`);
  add("Runs in", desktop ? `the desktop app ${f.app.desktop || "(version unknown)"} · ${ua.webview}` : `a browser · ${ua.browser}`);
  add("System", ua.os);
  if (f.screen) add("Screen", `${f.screen.w}×${f.screen.h} at ${Math.round(f.screen.dpr * 100) / 100}x`);
  add("Running for", duration(f.uptimeS));
  if (f.memory) {
    const m = f.memory, parts: string[] = [];
    if (m.usedMB !== undefined) parts.push(`JS heap ${m.usedMB} MB${m.limitMB ? ` of ${m.limitMB} MB` : ""}`);
    if (m.deviceGB) parts.push(`device ${m.deviceGB} GB`);
    if (m.cpus) parts.push(`${m.cpus} CPU threads`);
    add("Memory", parts.join(" · "));
  }
  if (f.webgl === null) add("Graphics", "no WebGL 2");
  else if (f.webgl) {
    const g = f.webgl, parts: string[] = [];
    parts.push(r(g.renderer) || "renderer unknown");
    if (g.vendor) parts.push(r(g.vendor));
    if (g.version) parts.push(r(g.version));
    if (g.maxTextureSize) parts.push(`max texture ${g.maxTextureSize}`);
    if (g.maxArrayLayers) parts.push(`array layers ${g.maxArrayLayers}`);
    if (g.max3dTextureSize) parts.push(`3D ${g.max3dTextureSize}`);
    if (g.maxTextureUnits) parts.push(`${g.maxTextureUnits} texture units`);
    if (g.maxRenderbufferSize) parts.push(`renderbuffer ${g.maxRenderbufferSize}`);
    if (g.contextLost) parts.push("context lost");
    add("Graphics", parts.join(" · "));
  }
  if (f.world) {
    const w = f.world, parts: string[] = [];
    if (w.demo) parts.push("the demo island");
    if (w.version || w.dataVersion) parts.push(`Minecraft ${r(w.version) || "?"}${w.dataVersion ? ` (DataVersion ${w.dataVersion})` : ""}`);
    if (w.layout) parts.push(w.layout === "dimensions" ? "26.x layout (dimensions/)" : "1.18–1.21 layout");
    if (w.modded !== undefined) parts.push(`modded: ${yesNo(w.modded)}`);
    if (w.dimensions !== undefined) parts.push(plural(w.dimensions, "dimension"));
    if (w.regionsHere !== undefined) parts.push(`${plural(w.regionsHere, "region")} in this dimension`);
    if (w.streamed !== undefined) parts.push(`streamed from a shared world: ${yesNo(w.streamed)}`);
    if (w.mapColoursOnly) parts.push("map colours only");
    add("World", parts.join(" · "));
  } else add("World", "none open");
  if (f.view) {
    const v = f.view;
    add("3D view", `${plural(v.chunks, "chunk")} · ${v.regionsLoaded}/${v.regionsTotal} region maps · ${v.pending} pending · ${num(v.triangles)} triangles · ${v.fps} fps${v.renderDistance ? ` · detail ${v.renderDistance} chunks` : ""}`);
  }
  if (f.plan) {
    const p = f.plan;
    const kinds = Object.entries(p.byKind).filter(([, n]) => n > 0).map(([k, n]) => plural(n, k)).join(", ");
    add("Plan", `${plural(p.objects, "object")}${kinds ? ` (${kinds})` : ""} · ${plural(p.groups, "group")} · ${plural(p.schematicBuildings, "schematic building")} · ${p.here} in this dimension`);
    const live: string[] = [];
    if (p.live) live.push(p.live);
    if (p.peers) live.push(`${plural(p.peers, "other person", "other people")} here`);
    if (p.role) live.push(`role ${p.role}`);
    if (p.readOnly) live.push("view only");
    add("Live", live.join(" · "));
  }
  if (f.ui) {
    const u = f.ui, parts = [`step ${u.phase}`, `tool ${u.tool}`];
    if (u.panel) parts.push(`panel ${u.panel}`);
    if (u.board) parts.push("pinboard open");
    if (u.help) parts.push("shortcuts open");
    if (u.slice) parts.push("slice Y on");
    if (u.topDown) parts.push("top-down");
    if (u.lighting) parts.push("day/night on");
    add("Open", parts.join(" · "));
  }
  const head = lines.length;

  const detailsFor = (): Record<string, unknown> => ({
    where: f.where,
    ...(f.repeats ? { repeats: f.repeats } : {}),
    stack,
    ...(componentStack ? { componentStack } : {}),
    log,
    uptimeS: Math.round(f.uptimeS),
    runtime: { kind: desktop ? "desktop" : "web", version: f.app.version, build: build || undefined, desktop: f.app.desktop || undefined, browser: ua.browser, os: ua.os, webview: ua.webview ?? undefined },
    ...(f.screen ? { screen: f.screen } : {}),
    ...(f.memory ? { memory: f.memory } : {}),
    webgl: f.webgl === null ? null : f.webgl ? { ...f.webgl, renderer: f.webgl.renderer && r(f.webgl.renderer), vendor: f.webgl.vendor && r(f.webgl.vendor), version: f.webgl.version && r(f.webgl.version) } : undefined,
    world: f.world ? { ...f.world, version: f.world.version && r(f.world.version) } : null,
    ...(f.view ? { view: f.view } : {}),
    ...(f.plan ? { plan: f.plan } : {}),
    ...(f.ui ? { ui: f.ui } : {}),
  });
  // `details` must stay under the account site's 100 KB as Postgres stores it: the log goes first, then the component
  // stack, then the stack is shortened. The report text is made from what is left, so it shows what is sent.
  let details = detailsFor();
  if (storedJsonBytes(details) > MAX_DETAILS_BYTES) { log = log.slice(-5); details = detailsFor(); }
  if (storedJsonBytes(details) > MAX_DETAILS_BYTES) { log = []; details = detailsFor(); }
  if (storedJsonBytes(details) > MAX_DETAILS_BYTES) { componentStack = ""; details = detailsFor(); }
  while (storedJsonBytes(details) > MAX_DETAILS_BYTES && stack.length > 500) { stack = cut(stack, Math.floor(stack.length / 2)); details = detailsFor(); }
  lines.length = head;
  lines.push("", "Stack:", stack || "(none)");
  if (componentStack) lines.push("", "Component stack:", componentStack);
  lines.push("", `Recent console errors and warnings (${log.length}):`, ...(log.length ? log : ["(none)"]));
  const report = cut(lines.join("\n"), MAX_REPORT);
  return { kind: f.kind, component: desktop ? "desktop" : "web", title, fingerprint: fp, appVersion, platform, report, details };
}

/** The JSON size the account site counts: Postgres stores `details` as jsonb, whose text has a space after every ":" and ",". */
export function storedJsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value) ?? "").length + separators(value);
}

function separators(value: unknown): number {
  if (Array.isArray(value)) return value.reduce((sum: number, item) => sum + separators(item), Math.max(0, value.length - 1));
  if (value && typeof value === "object") {
    const items = Object.values(value).filter((item) => item !== undefined);
    return items.reduce((sum: number, item) => sum + separators(item), Math.max(0, items.length * 2 - 1));
  }
  return 0;
}

/** The body of POST /api/v1/crash-reports (CRASH-REPORTS.md). The note is what the person typed, as typed. */
export function crashRequestBody(r: CrashReport, note = ""): Record<string, unknown> {
  const n = note.trim().slice(0, MAX_NOTE);
  return {
    app: "mcplanner",
    component: r.component,
    kind: r.kind,
    title: r.title,
    ...(r.fingerprint ? { fingerprint: r.fingerprint } : {}),
    appVersion: r.appVersion,
    platform: r.platform,
    report: r.report,
    details: r.details,
    ...(n ? { note: n } : {}),
  };
}

export type SendResult = { ok: true; id: string | null; repeat: boolean } | { ok: false; status: number; message: string };

/**
 * Posts the report once (no retries: the person presses Send again). No cookies, no sign-in, no referrer: only the
 * report goes to auth.lab43.dev.
 */
export async function sendCrashReport(
  body: Record<string, unknown>,
  opts: { url?: string; fetch?: typeof fetch; timeoutMs?: number } = {},
): Promise<SendResult> {
  const doFetch = opts.fetch ?? fetch;
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 20_000) : null;
  let res: Response;
  try {
    res = await doFetch(opts.url ?? CRASH_REPORTS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
      mode: "cors",
      cache: "no-store",
      referrerPolicy: "no-referrer",
      signal: ctrl?.signal,
    });
  } catch {
    return { ok: false, status: 0, message: "Couldn't reach Lab43. Check your connection, then press Send again." };
  } finally {
    if (timer) clearTimeout(timer);
  }
  let data: { id?: unknown; repeat?: unknown; message?: unknown } = {};
  try {
    data = (await res.json()) as typeof data;
  } catch { /* not JSON */ }
  if (res.status === 200 || res.status === 201) return { ok: true, id: typeof data.id === "string" ? data.id : null, repeat: data.repeat === true };
  const said = typeof data.message === "string" && data.message.trim() ? data.message.trim().slice(0, 300) : null;
  const message =
    res.status === 400 ? `Lab43 didn't accept the report${said ? `: ${said}` : "."}`
    : res.status === 404 ? "Lab43 isn't taking crash reports from MC Planner right now."
    : res.status === 413 ? "The report is too large to send."
    : res.status === 429 ? (said ?? "Too many reports from this address. Please try again later.")
    : `Lab43 couldn't take the report right now (error ${res.status}). Please try again later.`;
  return { ok: false, status: res.status, message };
}
